import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    model,
    signal,
    viewChild,
} from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { MultiSelectOption } from '../../models/multi-select-option';

/**
 * Multi-select with a bulk select all / deselect all toggle and a one-step undo of that bulk action.
 *
 * The label is passed in already translated so the component stays app agnostic; its own chrome
 * (buttons, tooltip, validation message) uses the shared translation namespace.
 */
@Component({
    selector: 'apcam-multi-select-toggle-all',
    templateUrl: './multi-select-toggle-all.component.html',
    styleUrl: './multi-select-toggle-all.component.scss',
    imports: [
        FormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatSelectModule,
        MatTooltipModule,
        TranslatePipe,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiSelectToggleAllComponent {
    readonly label = input.required<string>();
    readonly options = input.required<readonly MultiSelectOption[]>();
    readonly value = model.required<string[]>();

    /** Renders the required error once the user leaves an empty selection behind. */
    readonly required = input(false);

    /**
     * Values the user must not remove. Their options render disabled-but-checked and every bulk
     * action keeps them, so the caller does not have to re-add them on save. Values that are not
     * part of `options` cannot be locked and are ignored.
     */
    readonly lockedValues = input<readonly string[]>([]);

    /** Optional subscript text, e.g. to explain why an option cannot be unchecked. */
    readonly hint = input('');

    private readonly selectModel = viewChild.required(NgModel);

    /** Selection captured right before the last bulk action; null when there is nothing to undo. */
    private readonly snapshot = signal<string[] | null>(null);

    protected readonly canUndo = computed(() => this.snapshot() !== null);

    protected readonly lockedSet = computed(() => new Set(this.lockedValues()));

    /** Locked values narrowed to the ones that actually exist as options, in option order. */
    private readonly lockedOptionValues = computed(() => {
        const locked = this.lockedSet();
        return this.options()
            .filter((option) => locked.has(option.value))
            .map((option) => option.value);
    });

    protected readonly allSelected = computed(() => {
        const selected = new Set(this.value());
        const options = this.options();
        return (
            options.length > 0 &&
            options.every((option) => selected.has(option.value))
        );
    });

    /** Selected names in option order so the trigger summary does not reorder while picking. */
    protected readonly selectedNames = computed(() => {
        const selected = new Set(this.value());
        return this.options()
            .filter((option) => selected.has(option.value))
            .map((option) => option.name);
    });

    protected toggleAll(): void {
        this.snapshot.set([...this.value()]);
        this.value.set(
            this.allSelected()
                ? // Deselecting keeps the locked values - they are the floor of the selection.
                  [...this.lockedOptionValues()]
                : this.options().map((option) => option.value)
        );
        // A bulk change never blurs the select, so mark it touched by hand - otherwise the
        // required error would stay hidden after deselecting everything.
        this.selectModel().control.markAsTouched();
    }

    protected undo(): void {
        const snapshot = this.snapshot();
        if (snapshot === null) {
            return;
        }
        this.value.set(this.withLocked(snapshot));
        this.snapshot.set(null);
    }

    /** Any manual pick invalidates the snapshot - undo only ever reverts the last bulk action. */
    protected onSelectionChange(value: string[]): void {
        this.snapshot.set(null);
        this.value.set(this.withLocked(value));
    }

    /**
     * Restores any locked value the incoming selection is missing. The disabled options already stop
     * the user from unchecking them, so this only ever corrects a selection that arrived incomplete.
     */
    private withLocked(value: readonly string[]): string[] {
        const selected = new Set(value);
        const missing = this.lockedOptionValues().filter(
            (locked) => !selected.has(locked)
        );
        return missing.length === 0 ? [...value] : [...value, ...missing];
    }
}
